import axios, { AxiosError } from 'axios';
import attachAuthToken from './axiosConfig';
import type { ChecklistConfig, ChecklistType, MediaCategory } from '../types/checklist';
import type { ChecklistItemAnswer, ChecklistItemTemplate } from '../modules/checklist/types/checklistItems';

export type TipoServico = ChecklistType;

const authHeader = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  withCredentials: true,
});

attachAuthToken(api);

const errMsg = (e: unknown, fallback: string) => {
  if (axios.isAxiosError(e)) {
    const ax = e as AxiosError<any>;
    // Sem resposta HTTP = problema de conexão (ou arquivo que sumiu do disco
    // durante o envio) — "Network Error" cru não orienta ninguém.
    if (!ax.response) {
      return 'Falha de conexão ao comunicar com o servidor. Verifique sua internet e tente novamente.';
    }
    // at-64 (BUG-V68): com `responseType: 'blob'`, o corpo do ERRO também vem como Blob,
    // então `data.message` é sempre `undefined` e o texto caía no `ax.message` cru
    // ("Request failed with status code 429") — exibido inclusive ao cliente na tela
    // pública do checklist. Aqui não dá para ler o Blob (é assíncrono e esta função é
    // síncrona), então o fallback por status é o que resta: sempre melhor que o cru.
    if (ax.response.data instanceof Blob) {
      const status = ax.response.status;
      if (status === 429) return 'Muitas tentativas em pouco tempo. Aguarde alguns minutos e tente novamente.';
      if (status === 401 || status === 403) return 'Não foi possível confirmar o acesso a este checklist. Confira o número informado e tente novamente.';
      if (status === 404) return 'Checklist não encontrado ou link expirado.';
      if (status >= 500) return 'O servidor não conseguiu gerar o PDF agora. Tente novamente em instantes.';
      return fallback;
    }
    const rawMsg = ax.response?.data?.message || ax.message || fallback;
    return Array.isArray(rawMsg) ? rawMsg.join(', ') : String(rawMsg);
  }
  return fallback;
};

const uploadErrMsg = (e: unknown, fallback: string) => {
  if (axios.isAxiosError(e) && !(e as AxiosError).response) {
    return 'O envio falhou sem resposta do servidor. Verifique sua internet e tente novamente — se o arquivo foi movido ou apagado depois de anexado, anexe-o de novo. As mídias pendentes não são perdidas enquanto esta janela ficar aberta.';
  }
  return errMsg(e, fallback);
};

/** Timeout por request de upload de imagem (o retry cobre falha pontual de rede). */
const IMAGE_UPLOAD_TIMEOUT_MS = 120_000;

/** Repete uma vez em falha de rede (sem resposta HTTP) — não repete erros do servidor. */
const withNetworkRetry = async <T>(request: () => Promise<T>): Promise<T> => {
  try {
    return await request();
  } catch (e) {
    if (axios.isAxiosError(e) && !(e as AxiosError).response) {
      return request();
    }
    throw e;
  }
};

export async function getChecklistSessionsByCar(carId: string) {
  try {
    const { data } = await api.get(`/checklists/cars/${carId}/sessions`, { headers: authHeader() });
    return Array.isArray(data) ? data : [];
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao buscar sessões de checklist do veículo'));
  }
}

export async function createAndCompleteChecklist(dto: {
  carId: string;
  tipoServico: TipoServico;
  hasDamages?: boolean;
  /** Observações gerais do checklist (at-50) — até 10.000 caracteres (at-58). */
  observation?: string;
  items?: ChecklistItemAnswer[];
}) {
  try {
    const { data } = await api.post(
      '/checklists/sessions/create-and-complete',
      dto,
      { headers: authHeader() }
    );
    // appliedCarStatus é null nos checklists da família entrada (não movem o carro)
    return data as { sessionId: string; carId: string; appliedCarStatus: string | null };
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao salvar checklist'));
  }
}

export async function getChecklistSession(sessionId: string) {
  try {
    const { data } = await api.get(`/checklists/sessions/${sessionId}`, { headers: authHeader() });
    return data;
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao buscar sessão de checklist'));
  }
}

export async function getChecklistImages(sessionId: string) {
  try {
    const { data } = await api.get(`/checklists/sessions/${sessionId}/images`, { headers: authHeader() });
    return Array.isArray(data) ? data : [];
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao buscar imagens do checklist'));
  }
}

export async function getLatestChecklistSessionByCar(carId: string, filters?: { type?: ChecklistType; status?: string }) {
  try {
    const params: Record<string, string> = {};
    if (filters?.type) params.type = filters.type;
    if (filters?.status) params.status = filters.status;

    const { data } = await api.get(`/checklists/cars/${carId}/latest-session`, {
      headers: authHeader(),
      params,
    });
    return data as { id: string; carId: string; type: ChecklistType; status: string; createdAt: string; completedAt?: string | null };
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao buscar checklist do veículo'));
  }
}

/** Registra o início de um checklist na auditoria (sem criar rascunho). */
export async function trackChecklistStart(carId: string, tipoServico: TipoServico) {
  try {
    const { data } = await api.post(
      `/checklists/cars/${carId}/track-start`,
      { tipoServico },
      { headers: authHeader() }
    );
    return data as { tracked: boolean };
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao registrar o início do checklist'));
  }
}

export async function getChecklistConfig() {
  try {
    const { data } = await api.get('/checklists/config', { headers: authHeader() });
    return data as ChecklistConfig;
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao carregar a configuração do checklist'));
  }
}

export async function uploadChecklistMedia(
  sessionId: string,
  category: MediaCategory,
  files: File[],
  onUploadProgress?: (percent: number) => void,
  options?: { sectionName?: string; editBatchId?: string }
) {
  const formData = new FormData();
  formData.append('category', category);
  if (options?.sectionName) {
    formData.append('sectionName', options.sectionName);
  }
  if (options?.editBatchId) {
    formData.append('editBatchId', options.editBatchId);
  }
  for (const file of files) {
    formData.append('files', file);
  }

  try {
    const { data } = await withNetworkRetry(() =>
      api.post(`/checklists/sessions/${sessionId}/media/bulk`, formData, {
        headers: authHeader(),
        timeout: IMAGE_UPLOAD_TIMEOUT_MS,
        onUploadProgress: (progressEvent) => {
          if (onUploadProgress && progressEvent.total) {
            const percent = (progressEvent.loaded / progressEvent.total) * 100;
            onUploadProgress(Math.round(percent));
          }
        },
      })
    );
    return data as any[];
  } catch (e) {
    throw new Error(uploadErrMsg(e, 'Erro ao enviar mídias do checklist'));
  }
}

export async function uploadAngleSlot(
  sessionId: string,
  slotKey: string,
  files: File[],
  onUploadProgress?: (percent: number) => void,
  editBatchId?: string
) {
  const formData = new FormData();
  formData.append('category', 'ANGLES');
  formData.append('slot', slotKey);
  if (editBatchId) {
    formData.append('editBatchId', editBatchId);
  }
  for (const file of files) {
    formData.append('files', file);
  }

  try {
    const { data } = await withNetworkRetry(() =>
      api.post(`/checklists/sessions/${sessionId}/media/bulk`, formData, {
        headers: authHeader(),
        timeout: IMAGE_UPLOAD_TIMEOUT_MS,
        onUploadProgress: (progressEvent) => {
          if (onUploadProgress && progressEvent.total) {
            const percent = (progressEvent.loaded / progressEvent.total) * 100;
            onUploadProgress(Math.round(percent));
          }
        },
      })
    );
    return data as any[];
  } catch (e) {
    throw new Error(uploadErrMsg(e, 'Erro ao enviar fotos do ângulo'));
  }
}

/**
 * Teto do fallback de vídeo via backend — igual ao limite multipart do backend
 * (CHECKLIST_MULTIPART_MAX_BYTES, 30MB; acima de ~32MB a request morre no Cloud Run).
 */
const MULTIPART_VIDEO_FALLBACK_MAX_BYTES = 30 * 1024 * 1024;

/**
 * Fallback: envia o vídeo pela rota multipart do backend (a mesma das imagens),
 * usada quando o PUT direto ao GCS é bloqueado pela rede local (proxy/antivírus
 * interceptando o preflight CORS de storage.googleapis.com — incidentes da
 * recepção em 24 e 27/07/2026). A rota bulk já aceita video/* até 30MB.
 */
async function uploadChecklistVideoViaBackend(
  sessionId: string,
  category: MediaCategory,
  file: File,
  options?: { slot?: string; sectionName?: string; editBatchId?: string },
  onUploadProgress?: (percent: number) => void
) {
  const created =
    category === 'ANGLES' && options?.slot
      ? await uploadAngleSlot(sessionId, options.slot, [file], onUploadProgress, options?.editBatchId)
      : await uploadChecklistMedia(sessionId, category, [file], onUploadProgress, {
          sectionName: options?.sectionName,
          editBatchId: options?.editBatchId,
        });
  return Array.isArray(created) ? created[0] : created;
}

/**
 * Upload direto de vídeo ao GCS (URL assinada): vídeos estouram o limite de
 * request da plataforma (~32MB) e não passam pelo backend. Fluxo:
 * prepare (backend) → PUT no GCS → confirm (backend registra a mídia).
 *
 * Se o PUT direto falhar sem resposta HTTP (assinatura de bloqueio de rede/CORS
 * — o prepare no backend funcionou 1s antes, então internet há) e o vídeo couber
 * no multipart (≤ 30MB), reenvia automaticamente via backend.
 */
export async function uploadChecklistVideoDirect(
  sessionId: string,
  category: MediaCategory,
  file: File,
  options?: { slot?: string; sectionName?: string; editBatchId?: string },
  onUploadProgress?: (percent: number) => void
) {
  let prepared: { uploadUrl: string; storageKey: string; contentType: string };
  try {
    const { data } = await api.post(
      `/checklists/sessions/${sessionId}/media/prepare-upload`,
      {
        category,
        slot: options?.slot,
        sectionName: options?.sectionName,
        fileName: file.name,
        contentType: file.type || 'video/mp4',
        size: file.size,
      },
      { headers: authHeader() }
    );
    prepared = data;
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao preparar o envio do vídeo'));
  }

  try {
    // PUT direto no GCS — sem credenciais da API (a URL assinada é a autorização).
    // Sem timeout: vídeo grande em conexão lenta é um envio legítimo e demorado.
    await axios.put(prepared.uploadUrl, file, {
      headers: { 'Content-Type': prepared.contentType },
      onUploadProgress: (progressEvent) => {
        if (onUploadProgress && progressEvent.total) {
          const percent = (progressEvent.loaded / progressEvent.total) * 100;
          onUploadProgress(Math.round(percent));
        }
      },
    });
  } catch (e) {
    const blockedByNetwork = axios.isAxiosError(e) && !(e as AxiosError).response;

    if (blockedByNetwork && file.size <= MULTIPART_VIDEO_FALLBACK_MAX_BYTES) {
      return uploadChecklistVideoViaBackend(sessionId, category, file, options, onUploadProgress);
    }

    if (blockedByNetwork && file.size > MULTIPART_VIDEO_FALLBACK_MAX_BYTES) {
      throw new Error(
        'A rede desta máquina está bloqueando o envio direto de vídeos ao armazenamento (storage.googleapis.com), e o vídeo é grande demais para ir pelo servidor (acima de 30MB). Tente um vídeo menor ou acione a TI para liberar o acesso. As mídias pendentes não são perdidas enquanto esta janela ficar aberta.'
      );
    }

    throw new Error(uploadErrMsg(e, 'Erro ao enviar o vídeo para o armazenamento'));
  }

  try {
    const { data } = await api.post(
      `/checklists/sessions/${sessionId}/media/confirm-upload`,
      {
        category,
        slot: options?.slot,
        sectionName: options?.sectionName,
        storageKey: prepared.storageKey,
        fileName: file.name,
        editBatchId: options?.editBatchId,
      },
      { headers: authHeader() }
    );
    return data;
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao confirmar o envio do vídeo'));
  }
}

/** Upload de mídias para uma seção personalizada (nome livre criado pelo usuário). */
export async function uploadCustomSection(
  sessionId: string,
  sectionName: string,
  files: File[],
  onUploadProgress?: (percent: number) => void,
  editBatchId?: string
) {
  return uploadChecklistMedia(sessionId, 'CUSTOM', files, onUploadProgress, { sectionName, editBatchId });
}

/** PDF do checklist do atendimento (itens + miniaturas; unificado com imagens de saída). */
export async function downloadChecklistPdf(carId: string): Promise<Blob> {
  try {
    const { data } = await api.get(`/checklists/cars/${carId}/pdf`, {
      headers: authHeader(),
      responseType: 'blob',
    });
    return data as Blob;
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao gerar o PDF do checklist'));
  }
}

export interface ChecklistShareResult {
  token: string;
  expiresAt: string;
  ownerName: string | null;
  ownerPhone: string | null;
  plate: string;
  template: string;
  /**
   * at-126: o token do acompanhamento do associado, criado na mesma ação. `null` quando o servidor
   * não conseguiu criá-lo — a mensagem sai só com o checklist. Opcional porque o backend anterior
   * à at-126 não o devolve.
   */
  acompanhamento?: { token: string; ultimoAcessoEm: string | null } | null;
}

/** Gera (ou reaproveita) o token de compartilhamento do checklist + template da mensagem. */
export async function createChecklistShare(carId: string): Promise<ChecklistShareResult> {
  try {
    const { data } = await api.post(`/checklists/cars/${carId}/share`, {}, { headers: authHeader() });
    return data as ChecklistShareResult;
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao gerar o link de compartilhamento do checklist'));
  }
}

// ------- Rotas públicas do link compartilhado (sem autenticação) -------

const publicApi = axios.create({ baseURL: process.env.REACT_APP_BACKEND_URL });

export async function getPublicChecklistShareInfo(token: string) {
  try {
    const { data } = await publicApi.get(`/public/checklist-share/${token}`);
    return data as { valid: boolean; expiresAt: string };
  } catch (e) {
    throw new Error(errMsg(e, 'Link de checklist inválido ou expirado.'));
  }
}

export interface PublicChecklistView {
  car: { plate: string; model: string | null; marca: string | null; color: string | null; ownerName: string | null };
  entrada: any | null;
  saida: any | null;
}

export async function verifyPublicChecklistShare(token: string, phone: string): Promise<PublicChecklistView> {
  try {
    const { data } = await publicApi.post(`/public/checklist-share/${token}/verify`, { phone });
    return data as PublicChecklistView;
  } catch (e) {
    throw new Error(errMsg(e, 'Não foi possível verificar o número informado.'));
  }
}

/** PDF do checklist na tela pública — mesma verificação de telefone do conteúdo em tela. */
export async function downloadPublicChecklistPdf(token: string, phone: string): Promise<Blob> {
  try {
    const { data } = await publicApi.post(
      `/public/checklist-share/${token}/pdf`,
      { phone },
      { responseType: 'blob' }
    );
    return data as Blob;
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao gerar o PDF do checklist'));
  }
}

export async function deleteChecklistMedia(mediaId: string, editBatchId?: string) {
  try {
    const { data } = await api.delete(`/checklists/media/${mediaId}`, {
      headers: authHeader(),
      params: editBatchId ? { editBatchId } : undefined,
    });
    return data;
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao remover mídia do checklist'));
  }
}

export interface DeleteChecklistMediaBatchResult {
  /** Ids removidos de fato nesta chamada. */
  excluidas: string[];
  /** Ids que já não existiam no servidor (removidos em outra aba, por exemplo). */
  naoEncontradas: string[];
}

/**
 * Exclusão em massa (at-58): uma chamada para N mídias, em vez de N deletes.
 * Em edição de checklist concluído (editBatchId), a Auditoria registra o lote
 * em uma única linha.
 */
export async function deleteChecklistMediaBatch(
  mediaIds: string[],
  editBatchId?: string
): Promise<DeleteChecklistMediaBatchResult> {
  try {
    const { data } = await api.delete('/checklists/media', {
      headers: authHeader(),
      data: { mediaIds, ...(editBatchId ? { editBatchId } : {}) },
    });
    return data as DeleteChecklistMediaBatchResult;
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao remover as mídias do checklist'));
  }
}

export async function getChecklistItems(sessionId: string) {
  try {
    const { data } = await api.get(`/checklists/sessions/${sessionId}/items`, { headers: authHeader() });
    return data as { templates: ChecklistItemTemplate[]; answers: ChecklistItemAnswer[] };
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao buscar itens do checklist'));
  }
}

/**
 * Edição de sessão concluída: respostas alteradas e/ou observações gerais.
 * `observation` só entra no payload quando informado — ausente não mexe no
 * valor atual; string vazia limpa (at-50).
 */
export async function saveChecklistItems(
  sessionId: string,
  answers: ChecklistItemAnswer[],
  editBatchId?: string,
  observation?: string
) {
  try {
    const { data } = await api.put(
      `/checklists/sessions/${sessionId}/items`,
      {
        answers,
        ...(editBatchId ? { editBatchId } : {}),
        ...(observation !== undefined ? { observation } : {}),
      },
      { headers: authHeader() }
    );
    return data as { answers: ChecklistItemAnswer[]; observation: string | null };
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao salvar itens do checklist'));
  }
}

export interface GlobalChecklistQuery {
  page: number;
  limit: number;
  plate?: string;
  type?: ChecklistType;
  startDate?: string;
  endDate?: string;
}

export async function getGlobalChecklists(query: GlobalChecklistQuery) {
  try {
    const { data } = await api.get('/checklists/sessions', {
      headers: authHeader(),
      params: query,
    });
    return data as {
      total: number;
      page: number;
      limit: number;
      items: Array<{
        id: string;
        carId: string;
        type: ChecklistType;
        status: string;
        createdAt: string;
        completedAt?: string | null;
        car: { plate: string; model: string };
        createdBy?: { fullName: string } | null;
      }>;
    };
  } catch (e) {
    throw new Error(errMsg(e, 'Erro ao buscar histórico global de checklists'));
  }
}
